// @ts-check
// Seção "Módulos" no detalhe da escola do painel de suporte (3.0.0, contrato 10 de atendimento, seção 2.5): interruptor
// por módulo, origem ("Ligado pelo suporte", "Ligado pelo plano", "Padrão do ambiente"), nota, quem mudou e quando, e a
// confirmação ao desligar com o que acontece (seção 2.8). Com casos abertos da Ouvidoria ou da Proteção, o servidor pede
// uma segunda confirmação (409 RESTRICTED_TICKETS_OPEN) e a tela mostra o aviso com a contagem antes de reenviar com
// confirmRestricted. Só para SUPERADMIN_EMAILS (o servidor confere).
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { fetchAdminTenantModules, setAdminTenantModule } from '../../data/tenant-modules.js';
import { formatDateTime } from '../../lib/format.js';
import { Skeleton, Toggle } from '../../ui/feedback.js';
import { Modal } from '../../ui/modal.js';
import { showSuccessSnackbar } from '../../ui/snackbar.js';
import { showOperationsError } from './operations-format.js';
import { t } from '../../i18n/index.js';
import { MODULE_LABEL_KEYS, MODULE_NOTE_MAX_LENGTH } from '../../../../shared/modules/tenant-modules.js';

/** @typedef {import('../../data/tenant-modules.js').AdminTenantModule} AdminTenantModule */

/** Rótulo da origem: a linha do suporte desligada diz "Desligado pelo suporte". @param {AdminTenantModule} module */
export function tenantModuleSourceLabel(module) {
  if (module.source === 'SUPPORT' && !module.isEnabled) return t('tenantModules.sources.SUPPORT_OFF');
  return t(`tenantModules.sources.${module.source}`);
}

/** @param {string} moduleKey */
const moduleLabel = (moduleKey) => t(/** @type {Record<string, string>} */ (MODULE_LABEL_KEYS)[moduleKey] ?? moduleKey);

/** @param {{ tenantId: string }} props */
export function TenantModulesSection({ tenantId }) {
  const [modules, setModules] = useState(/** @type {AdminTenantModule[] | null} */ (null));
  const [notes, setNotes] = useState(/** @type {Record<string, string>} */ ({}));
  const [busyModuleKey, setBusyModuleKey] = useState(/** @type {string | null} */ (null));
  const [confirmDisable, setConfirmDisable] = useState(/** @type {string | null} */ (null));
  const [restrictedWarning, setRestrictedWarning] = useState(/** @type {{ moduleKey: string, count: number } | null} */ (null));

  useEffect(() => {
    setModules(null);
    setNotes({});
    fetchAdminTenantModules(tenantId).then(setModules).catch((error) => {
      setModules([]);
      showOperationsError(error);
    });
  }, [tenantId]);

  /** @param {string} moduleKey @param {boolean} isEnabled @param {{ confirmRestricted?: boolean }} [options] */
  const change = async (moduleKey, isEnabled, options = {}) => {
    setBusyModuleKey(moduleKey);
    try {
      const note = (notes[moduleKey] ?? '').trim();
      setModules(await setAdminTenantModule(tenantId, moduleKey, { isEnabled, note: note || null, ...(options.confirmRestricted ? { confirmRestricted: true } : {}) }));
      setNotes((current) => ({ ...current, [moduleKey]: '' }));
      setConfirmDisable(null);
      setRestrictedWarning(null);
      showSuccessSnackbar(t(isEnabled ? 'tenantModules.enabledSnackbar' : 'tenantModules.disabledSnackbar', { module: moduleLabel(moduleKey) }));
    } catch (error) {
      const apiError = /** @type {{ code?: string, details?: Record<string, any> }} */ (error);
      if (apiError?.code === 'RESTRICTED_TICKETS_OPEN') {
        setConfirmDisable(null);
        setRestrictedWarning({ moduleKey, count: Number(apiError.details?.openRestrictedTickets ?? 0) });
      } else showOperationsError(error);
    } finally {
      setBusyModuleKey(null);
    }
  };

  return html`<section class="admin-block" data-testid="tenant-modules-section">
    <h3 class="admin-block-title">${t('tenantModules.title')}</h3>
    <p class="channel-card-description">${t('tenantModules.description')}</p>
    ${modules === null
      ? html`<${Skeleton} height=${64} radius=${8} />`
      : html`<ul class="admin-list">${modules.map((module) => html`<li key=${module.moduleKey} data-module=${module.moduleKey}>
          <div class="channel-card-titles">
            <span class="channel-card-title">${moduleLabel(module.moduleKey)}</span>
            <span class="channel-card-description">${t(`tenantModules.moduleDescriptions.${module.moduleKey}`)}</span>
            <span class="settings-table-muted">
              ${module.isEnabled ? t('tenantModules.enabled') : t('tenantModules.disabled')} · ${tenantModuleSourceLabel(module)}
              ${module.changedByEmail && module.changedAt
                ? html` · ${t('tenantModules.changedBy', { email: module.changedByEmail, date: formatDateTime(module.changedAt) })}`
                : html` · ${t('tenantModules.neverChanged')}`}
            </span>
            ${module.note ? html`<span class="settings-table-muted">${module.note}</span>` : null}
            <input class="text-input" type="text" maxLength=${MODULE_NOTE_MAX_LENGTH} value=${notes[module.moduleKey] ?? ''}
              placeholder=${t('tenantModules.notePlaceholder')} aria-label=${t('tenantModules.noteLabel')}
              onInput=${(/** @type {Event} */ event) => setNotes((current) => ({ ...current, [module.moduleKey]: /** @type {HTMLInputElement} */ (event.currentTarget).value }))} />
          </div>
          <${Toggle}
            value=${module.isEnabled}
            disabled=${busyModuleKey === module.moduleKey}
            ariaLabel=${t('tenantModules.toggleLabel', { module: moduleLabel(module.moduleKey) })}
            onChange=${(/** @type {boolean} */ isEnabled) => (isEnabled ? change(module.moduleKey, true) : setConfirmDisable(module.moduleKey))}
          />
        </li>`)}</ul>`}
    ${confirmDisable
      ? html`<${Modal}
          title=${t('tenantModules.confirmDisableTitle', { module: moduleLabel(confirmDisable) })}
          subtitle=${t('tenantModules.confirmDisableDescription')}
          confirmLabel=${t('tenantModules.confirmDisable')}
          confirmAccent="danger"
          isLoading=${busyModuleKey === confirmDisable}
          onClose=${() => setConfirmDisable(null)}
          onConfirm=${() => change(/** @type {string} */ (confirmDisable), false)}
        />`
      : null}
    ${restrictedWarning
      ? html`<${Modal}
          title=${t('tenantModules.restrictedOpenTitle')}
          subtitle=${t('tenantModules.restrictedOpenDescription', { count: restrictedWarning.count })}
          confirmLabel=${t('tenantModules.restrictedOpenConfirm')}
          confirmAccent="danger"
          isLoading=${busyModuleKey === restrictedWarning.moduleKey}
          onClose=${() => setRestrictedWarning(null)}
          onConfirm=${() => change(restrictedWarning.moduleKey, false, { confirmRestricted: true })}
        />`
      : null}
  </section>`;
}
